import { apiClient } from '../client'
import type {
  CreateOrgRequest,
  InviteMemberRequest,
  LoginRequest,
  LoginResponse,
  MFAEnableResponse,
  MFABackupCodesResponse,
  MFAVerifyRequest,
  Org,
  OrgMember,
  OrgRole,
  ResetPasswordRequest,
  Session,
  SignupRequest,
  SignupResponse,
  User,
} from '@milliswift/types'

/**
 * Accounts and auth. Paths match platform/router/router.go.
 *
 * Note there is no `/signup` success body carrying a token: signing up sends a
 * verification email, and the user signs in afterwards.
 */
export const authApi = {
  signup: (data: SignupRequest) =>
    apiClient.post<SignupResponse>('/auth/signup', data).then((r) => r.data),

  login: (data: LoginRequest) =>
    apiClient.post<LoginResponse>('/auth/login', data).then((r) => r.data),

  logout: () => apiClient.post('/auth/logout').then(() => undefined),

  refresh: () => apiClient.post<LoginResponse>('/auth/refresh').then((r) => r.data),

  me: () => apiClient.get<User>('/auth/me').then((r) => r.data),

  verifyEmail: (token: string) =>
    apiClient.get('/auth/verify-email', { params: { token } }).then((r) => r.data),

  resendVerification: (email: string) =>
    apiClient.post('/auth/resend-verification', { email }).then((r) => r.data),

  forgotPassword: (email: string) =>
    apiClient.post('/auth/forgot-password', { email }).then((r) => r.data),

  resetPassword: (data: ResetPasswordRequest) =>
    apiClient.post('/auth/reset-password', data).then((r) => r.data),

  /* ------------------------------------------------------------- sessions */

  listSessions: () => apiClient.get<Session[]>('/auth/sessions').then((r) => r.data),

  revokeSession: (sessionId: string) =>
    apiClient.delete(`/auth/sessions/${sessionId}`).then(() => undefined),

  /* ------------------------------------------------------------------ MFA */

  enableMFA: () => apiClient.post<MFAEnableResponse>('/auth/mfa/enable').then((r) => r.data),

  /** Completes enrolment: the server returns the backup codes exactly once. */
  verifyMFA: (data: MFAVerifyRequest) =>
    apiClient.post<MFABackupCodesResponse & LoginResponse>('/auth/mfa/verify', data).then((r) => r.data),

  disableMFA: (code: string) =>
    apiClient.post('/auth/mfa/disable', { code }).then((r) => r.data),

  /* ----------------------------------------------------- account deletion */

  requestDeletion: () => apiClient.post('/auth/delete-account').then((r) => r.data),

  cancelDeletion: () => apiClient.post('/auth/delete-account/cancel').then((r) => r.data),
}

/**
 * Organisations. Creating and inviting require owner/admin; listing members is
 * readable by any member role.
 */
export const orgsApi = {
  create: (data: CreateOrgRequest) => apiClient.post<Org>('/orgs', data).then((r) => r.data),

  acceptInvite: (token: string) =>
    apiClient.post<Org>('/orgs/invites/accept', { token }).then((r) => r.data),

  inviteMember: (orgId: string, data: InviteMemberRequest) =>
    apiClient.post(`/orgs/${orgId}/invite`, data).then((r) => r.data),

  listMembers: (orgId: string) =>
    apiClient.get<OrgMember[]>(`/orgs/${orgId}/members`).then((r) => r.data),

  changeRole: (orgId: string, userId: string, role: OrgRole) =>
    apiClient.post(`/orgs/${orgId}/members/${userId}/role`, { role }).then((r) => r.data),

  removeMember: (orgId: string, userId: string) =>
    apiClient.delete(`/orgs/${orgId}/members/${userId}`).then(() => undefined),
}
